import { useDocumentRouteReady } from "../components/RouteCanvas";
import { ContextualPopover } from "../components/ContextualPopover";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";

import { useSession } from "../auth/session";
import { Button } from "../components/Button";
import { EmptyState, Notice, Skeleton } from "../components/Feedback";
import { RequestNotice } from "../components/RequestNotice";
import { SUBSCRIPTION_REQUIRED_MESSAGE } from "../subscriptions/access";
import { SegmentedControl } from "../components/FormControls";
import { RegisterHeader, RegisterStatusChip } from "../experience/RegisterPrimitives";
import { navigate, useLocationUrl } from "../routing/useLocationPath";
import { clientRequest, type ClientRecord, type RecordState } from "./clientApi";
import {
  clientCollectionComposition,
  clientRecordPath,
  updateClientInventory,
  type ClientInventory,
} from "./clientCollection";
import { clientMatch } from "./clientSearch";

type LoadState = "idle" | "loading" | "ready" | "refresh-error" | "blocking-error";

const collectionMemory: { focusId: string | null; query: string; scope: RecordState; scrollY: number } = {
  focusId: null,
  query: "",
  scope: "active",
  scrollY: 0,
};

function rememberCollection(clientId: string | null, query: string, scope: RecordState) {
  collectionMemory.focusId = clientId;
  collectionMemory.query = query;
  collectionMemory.scope = scope;
  collectionMemory.scrollY = window.scrollY;
}

export function ClientsPage() {
  const { getToken, sessionId, state: sessionState } = useSession();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const locationUrl=useLocationUrl();
  const [scope, setScope] = useState<RecordState>(collectionMemory.scope);
  const [query, setQuery] = useState(collectionMemory.query);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const clientsRef = useRef(clients);
  clientsRef.current = clients;
  const [inventory, setInventory] = useState<ClientInventory | null>(null);
  const inventoryRef = useRef(inventory);
  inventoryRef.current = inventory;
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [retryKey, setRetryKey] = useState(0);
  const [actionError, setActionError] = useState("");
  const [actionClient,setActionClient] = useState<ClientRecord | null>(null);
  const actionTrigger = useRef<HTMLButtonElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const load = useCallback(async (signal: AbortSignal) => {
    setLoadState((current) => current === "ready" || current === "refresh-error" ? current : "loading");
    const search = new URLSearchParams({ state: scope });
    if (query.trim()) search.set("query", query.trim());
    try {
      const inventoryRequest = inventoryRef.current ? null : Promise.all([
        clientRequest<{ clients: ClientRecord[] }>(getTokenRef.current, "/api/clients?state=active", { signal }),
        clientRequest<{ clients: ClientRecord[] }>(getTokenRef.current, "/api/clients?state=archived", { signal }),
      ]);
      const [result, inventoryResult] = await Promise.all([
        clientRequest<{ clients: ClientRecord[] }>(getTokenRef.current, `/api/clients?${search}`, { signal }),
        inventoryRequest,
      ]);
      if (signal.aborted) return;
      if (inventoryResult) {
        setInventory({ active: inventoryResult[0].clients.length, archived: inventoryResult[1].clients.length });
      }
      setClients(result.clients);
      setLoadState("ready");
      setActiveIndex(-1);
    } catch {
      if (!signal.aborted) setLoadState(clientsRef.current.length ? "refresh-error" : "blocking-error");
    }
  }, [query, scope]);

  useEffect(() => {
    if (sessionState !== "signed-in" || !sessionId) return;
    const controller = new AbortController();
    const delay = window.setTimeout(() => void load(controller.signal), query.trim() ? 180 : 0);
    return () => {
      window.clearTimeout(delay);
      controller.abort();
    };
  }, [load, retryKey, sessionId, sessionState]);

  useEffect(() => {
    if (loadState !== "ready") return;
    const frame = requestAnimationFrame(() => {
      window.scrollTo({ top: collectionMemory.scrollY });
      if (collectionMemory.focusId) document.getElementById(`client-option-${collectionMemory.focusId}`)?.focus();
      collectionMemory.focusId = null;
    });
    return () => cancelAnimationFrame(frame);
  }, [loadState]);

  useEffect(()=>{const requested=new URL(locationUrl,window.location.origin).searchParams.get("state");if(requested==="active"||requested==="archived"){if(requested===scope&&query){collectionMemory.query="";setQuery("");setActiveIndex(-1);}else changeScope(requested);}},[locationUrl]);

  function changeScope(next: RecordState) {
    if (next === scope) return;
    collectionMemory.scope = next;
    collectionMemory.query = "";
    collectionMemory.scrollY = 0;
    setScope(next);
    setQuery("");
    navigate(`/clients?state=${next}`);
    setClients([]);
    setLoadState("loading");
  }

  function openClient(clientId: string, event?: MouseEvent<HTMLAnchorElement>) {
    if (event && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event?.preventDefault();
    rememberCollection(clientId, query, scope);
    navigate(clientRecordPath(clientId));
  }

  function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setQuery("");
      setActiveIndex(-1);
      return;
    }
    if (!clients.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => (current + direction + clients.length) % clients.length);
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      const client = clients[activeIndex];
      if (client) openClient(client.id);
    }
  }

  function openCreate() {
    rememberCollection(null, query, scope);
    navigate("/clients/new");
  }

  async function changeClientState(client: ClientRecord) {
    const action = client.state === "active" ? "archive" : "restore";
    setActionError("");
    try {
      await clientRequest(getTokenRef.current, `/api/clients/${client.id}/${action}`, {
        method: "POST",
        body: JSON.stringify({ version: client.version }),
      });
      setInventory((current) => updateClientInventory(current, client.state, client.state === "active" ? "archived" : "active"));
      setRetryKey((value) => value + 1);
    } catch (failure) {
      if (failure instanceof Error && failure.message === SUBSCRIPTION_REQUIRED_MESSAGE) setActionError(failure.message);
      else setLoadState("refresh-error");
    }
  }

  const waitingForSession = sessionState !== "signed-in" || !sessionId;
  const showInitialLoading = waitingForSession || loadState === "idle" || loadState === "loading" && clients.length === 0;
  useDocumentRouteReady(!showInitialLoading);
  const composition = clientCollectionComposition(inventory);
  const searchExpanded = !showInitialLoading && loadState !== "blocking-error" && composition.showRegisterStructure && clients.length > 0 && Boolean(query.trim() || clients[activeIndex]);

  return (
    <div className="clients-page clients-mobile-register">
      <RegisterHeader date="Client register" title="Clients" description="Keep the people and businesses you bill, and the contacts who represent them, in one dependable register." action={<div className="row-actions"><Button variant="secondary" onClick={()=>{rememberCollection(null,query,scope);navigate("/clients/import");}}>Import CSV</Button>{composition.showHeaderAction ? <Button onClick={openCreate}>New client</Button> : null}</div>} />

      {composition.showDiscovery ? <section className="client-discovery field" aria-label="Find clients">
        <label htmlFor="client-search">Find a client or contact</label>
        <div className="sbx-register-tools">
        <div className="sbx-register-search">
        <svg aria-hidden="true" viewBox="0 0 20 20"><circle cx="8.5" cy="8.5" r="5.5"/><path d="m13 13 4 4"/></svg>
        <input
          id="client-search"
          className="client-search"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="client-results"
          aria-expanded={searchExpanded}
          aria-activedescendant={searchExpanded && clients[activeIndex] ? `client-option-${clients[activeIndex].id}` : undefined}
          placeholder="Search client, contact, email, or phone"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            collectionMemory.query = event.target.value;
            setActiveIndex(-1);
          }}
          onKeyDown={onSearchKeyDown}
        />
        </div>
        <SegmentedControl className="sbx-scope" label="Client state" value={scope} onChange={changeScope} options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }]} />
        </div>
      </section> : null}

      {composition.showRegisterStructure ? <p className="result-announcement" aria-live="polite">{showInitialLoading ? "Loading clients" : `${clients.length} ${clients.length === 1 ? "client" : "clients"}`}</p> : null}

      {actionError ? <RequestNotice title="Change not saved" message={actionError} /> : null}
      {loadState === "refresh-error" ? <Notice title="Clients could not refresh" tone="caution">Current results are retained. <Button variant="quiet" onClick={() => setRetryKey((value) => value + 1)}>Retry</Button></Notice> : null}
      {loadState === "blocking-error" ? <Notice title="Clients are unavailable" tone="danger">The register could not load. <Button variant="secondary" onClick={() => setRetryKey((value) => value + 1)}>Retry</Button></Notice> : null}

      {showInitialLoading ? (
        <div className="client-list client-list--loading" aria-hidden="true"><Skeleton height={72} /><Skeleton height={72} /><Skeleton height={72} /></div>
      ) : loadState !== "blocking-error" && composition.showRegisterStructure ? (
        <div id="client-results" className="client-list" role="listbox" aria-label={`${scope} clients`}>
          {clients.map((client, index) => {
            const match = query.trim() ? clientMatch(client, query) : null;
            return (
              <article key={client.id} className="client-row">
                <a id={`client-option-${client.id}`} className="client-row-link" href={`/clients/${client.id}`} role="option" aria-selected={activeIndex === index} onClick={(event) => openClient(client.id, event)}>
                  <span className="client-identity sbx-record-link">
                    <strong>{client.displayName}</strong>
                    {match && match !== "name" ? <small className="sbx-cell-secondary">Matched by {match === "contact" ? "primary contact" : match}</small> : null}
                  </span>
                  <span className="client-primary-contact">{client.primaryContact ? <><span className="client-contact-name">{client.primaryContact.name || client.primaryContact.email}</span><span className="sbx-cell-secondary">{client.primaryContact.email || "No email recorded"}</span></> : <span className="sbx-cell-secondary">No primary contact</span>}</span>
                  <span className="client-row-state"><RegisterStatusChip status={client.state === "active" ? "Active" : "Archived"} /></span>
                </a>
                <Button className="client-desktop-state-action" variant="quiet" onClick={() => void changeClientState(client)}>{client.state === "active" ? "Archive" : "Restore"}</Button><Button className="client-mobile-actions sbx-register-more" variant="quiet" aria-label={`Actions for ${client.displayName}`} aria-haspopup="dialog" aria-expanded={actionClient?.id === client.id} onClick={event => { actionTrigger.current=event.currentTarget;setActionClient(client); }}>•••</Button>
              </article>
            );
          })}
        </div>
      ) : null}

      {actionClient ? <ContextualPopover label="Client actions" anchorRef={actionTrigger} width={200} focusFirst className="client-mobile-action-menu" onClose={() => setActionClient(null)}><Button variant="quiet" onClick={() => { const client=actionClient;setActionClient(null);actionTrigger.current?.focus();void changeClientState(client); }}>{actionClient.state === "active" ? "Archive" : "Restore"}</Button></ContextualPopover> : null}

      {!showInitialLoading && loadState !== "blocking-error" && clients.length === 0 ? (
        <EmptyState title={composition.isVirgin ? "No clients yet" : query.trim() ? "No clients match this search" : scope === "active" ? "No active clients" : "No archived clients"} action={composition.showEmptyAction ? <Button onClick={openCreate}>New client</Button> : undefined}>
          {composition.isVirgin ? "Add your first client to keep billing details and contacts together." : query.trim() ? `No client or active contact matches “${query.trim()}”.` : scope === "active" ? "Archived clients remain available from the Archived scope." : "Archived clients will appear here and remain restorable."}
        </EmptyState>
      ) : null}
    </div>
  );
}
